class QBProductCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector("[data-carousel-track]");
    this.slides = this.carousel.querySelectorAll(".qb-product-carousel__slide");
    this.prevBtn = this.carousel.parentElement.querySelector(
      ".qb-carousel__nav-btn--prev"
    );
    this.nextBtn = this.carousel.parentElement.querySelector(
      ".qb-carousel__nav-btn--next"
    );

    this.currentIndex = 0;
    this.slidesToShow = this.getSlidesToShow();
    this.maxIndex = Math.max(0, this.slides.length - this.slidesToShow);

    this.init();
  }

  init() {
    if (this.slides.length <= this.slidesToShow) {
      this.prevBtn.style.display = "none";
      this.nextBtn.style.display = "none";
      return;
    }

    this.updateButtons();
    this.bindEvents();
    this.initActionButtons();

    // Handle window resize
    window.addEventListener(
      "resize",
      this.debounce(() => {
        this.slidesToShow = this.getSlidesToShow();
        this.maxIndex = Math.max(0, this.slides.length - this.slidesToShow);
        this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
        this.updateCarousel();
        this.updateButtons();
      }, 250)
    );
  }

  getSlidesToShow() {
    const width = window.innerWidth;
    if (width < 750) return 1.5; // Mobile - show partial next slide
    if (width < 990) return 3; // Tablet
    return 4; // Desktop - can be customized based on section settings
  }

  bindEvents() {
    this.prevBtn.addEventListener("click", () => this.goToPrev());
    this.nextBtn.addEventListener("click", () => this.goToNext());

    // Touch/swipe support
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
      },
      { passive: true }
    );

    this.track.addEventListener(
      "touchmove",
      (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.track.addEventListener("touchend", () => {
      if (!isDragging) return;
      isDragging = false;

      const diff = startX - currentX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.goToNext();
        } else {
          this.goToPrev();
        }
      }
    });

    // Keyboard support
    this.carousel.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.goToPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        this.goToNext();
      }
    });
  }

  initActionButtons() {
    // Handle wishlist buttons
    const wishlistBtns = this.carousel.querySelectorAll(
      ".qb-product-card__wishlist"
    );
    wishlistBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        btn.classList.toggle("active");

        const productId = btn.dataset.productId;
        if (btn.classList.contains("active")) {
          this.addToWishlist(productId);
        } else {
          this.removeFromWishlist(productId);
        }
      });
    });

    // Handle quick view buttons
    const quickviewBtns = this.carousel.querySelectorAll(
      ".qb-product-card__quickview"
    );
    quickviewBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productUrl = btn.dataset.productUrl;
        this.openQuickView(productUrl);
      });
    });

    // Handle compare buttons
    const compareBtns = this.carousel.querySelectorAll(
      ".qb-product-card__compare"
    );
    compareBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        btn.classList.toggle("active");

        const productId = btn.dataset.productId;
        if (btn.classList.contains("active")) {
          this.addToCompare(productId);
        } else {
          this.removeFromCompare(productId);
        }
      });
    });

    // Handle add to cart buttons
    const cartForms = this.carousel.querySelectorAll(
      ".qb-product-card__cart-form"
    );
    cartForms.forEach((form) => {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.handleAddToCart(form);
      });
    });
  }

  // Wishlist functionality
  addToWishlist(productId) {
    // Get existing wishlist from localStorage
    let wishlist = JSON.parse(localStorage.getItem("productWishlist") || "[]");

    // Add product if not already in wishlist
    if (!wishlist.includes(productId)) {
      wishlist.push(productId);
      localStorage.setItem("productWishlist", JSON.stringify(wishlist));

      // Show success message
      this.showNotification("Added to wishlist", "success");

      // Dispatch custom event
      document.dispatchEvent(
        new CustomEvent("wishlist:added", {
          detail: { productId },
        })
      );
    }
  }

  removeFromWishlist(productId) {
    let wishlist = JSON.parse(localStorage.getItem("productWishlist") || "[]");
    wishlist = wishlist.filter((id) => id !== productId);
    localStorage.setItem("productWishlist", JSON.stringify(wishlist));

    this.showNotification("Removed from wishlist", "info");

    document.dispatchEvent(
      new CustomEvent("wishlist:removed", {
        detail: { productId },
      })
    );
  }

  // Compare functionality
  addToCompare(productId) {
    let compareList = JSON.parse(
      localStorage.getItem("productCompare") || "[]"
    );

    // Limit to 3 products for comparison
    if (compareList.length >= 3) {
      this.showNotification("Maximum 3 products can be compared", "warning");
      return;
    }

    if (!compareList.includes(productId)) {
      compareList.push(productId);
      localStorage.setItem("productCompare", JSON.stringify(compareList));

      this.showNotification("Added to compare", "success");

      document.dispatchEvent(
        new CustomEvent("compare:added", {
          detail: { productId },
        })
      );
    }
  }

  removeFromCompare(productId) {
    let compareList = JSON.parse(
      localStorage.getItem("productCompare") || "[]"
    );
    compareList = compareList.filter((id) => id !== productId);
    localStorage.setItem("productCompare", JSON.stringify(compareList));

    this.showNotification("Removed from compare", "info");

    document.dispatchEvent(
      new CustomEvent("compare:removed", {
        detail: { productId },
      })
    );
  }

  // Quick view functionality
  openQuickView(productUrl) {
    // Create and show quick view modal
    this.showNotification("Opening quick view...", "info");

    // You can implement your quick view modal here
    // This is a basic example - you might want to fetch product data via AJAX
    document.dispatchEvent(
      new CustomEvent("quickview:open", {
        detail: { productUrl },
      })
    );

    console.log("Quick view opened for:", productUrl);
  }

  // Add to cart functionality
  async handleAddToCart(form) {
    const formData = new FormData(form);
    const button = form.querySelector(".qb-product-card__add-to-cart");
    const originalText = button.querySelector(
      ".qb-product-card__add-text"
    ).textContent;

    // Update button state
    button.disabled = true;
    button.querySelector(".qb-product-card__add-text").textContent =
      "Adding...";

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        this.showNotification("Added to cart successfully!", "success");

        // Update cart count if you have a cart indicator
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { item: data },
          })
        );
      } else {
        throw new Error("Failed to add to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      this.showNotification(
        "Failed to add to cart. Please try again.",
        "error"
      );
    } finally {
      // Reset button state
      button.disabled = false;
      button.querySelector(".qb-product-card__add-text").textContent =
        originalText;
    }
  }

  // Notification system
  showNotification(message, type = "info") {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = `qb-notification qb-notification--${type}`;
    notification.innerHTML = `
      <div class="qb-notification__content">
        <span class="qb-notification__message">${message}</span>
        <button class="qb-notification__close" aria-label="Close notification">×</button>
      </div>
    `;

    // Add styles if not already added
    this.addNotificationStyles();

    // Add to page
    document.body.appendChild(notification);

    // Show notification
    setTimeout(() => notification.classList.add("qb-notification--show"), 100);

    // Auto remove after 3 seconds
    setTimeout(() => this.removeNotification(notification), 3000);

    // Handle close button
    notification
      .querySelector(".qb-notification__close")
      .addEventListener("click", () => {
        this.removeNotification(notification);
      });
  }

  removeNotification(notification) {
    notification.classList.remove("qb-notification--show");
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }

  addNotificationStyles() {
    if (document.querySelector("#qb-notification-styles")) return;

    const styles = document.createElement("style");
    styles.id = "qb-notification-styles";
    styles.textContent = `
      .qb-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 10000;
        max-width: 350px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-left: 4px solid #007bff;
        transform: translateX(100%);
        transition: transform 0.3s ease;
      }
      
      .qb-notification--show {
        transform: translateX(0);
      }
      
      .qb-notification--success {
        border-left-color: #28a745;
      }
      
      .qb-notification--error {
        border-left-color: #dc3545;
      }
      
      .qb-notification--warning {
        border-left-color: #ffc107;
      }
      
      .qb-notification__content {
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      
      .qb-notification__message {
        font-size: 14px;
        color: #333;
        flex: 1;
      }
      
      .qb-notification__close {
        background: none;
        border: none;
        font-size: 18px;
        cursor: pointer;
        color: #666;
        margin-left: 12px;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .qb-notification__close:hover {
        color: #333;
      }
    `;

    document.head.appendChild(styles);
  }

  // Initialize saved states from localStorage
  initializeSavedStates() {
    const wishlist = JSON.parse(
      localStorage.getItem("productWishlist") || "[]"
    );
    const compareList = JSON.parse(
      localStorage.getItem("productCompare") || "[]"
    );

    // Update wishlist button states
    wishlist.forEach((productId) => {
      const wishlistBtn = this.carousel.querySelector(
        `[data-product-id="${productId}"].qb-product-card__wishlist`
      );
      if (wishlistBtn) {
        wishlistBtn.classList.add("active");
      }
    });

    // Update compare button states
    compareList.forEach((productId) => {
      const compareBtn = this.carousel.querySelector(
        `[data-product-id="${productId}"].qb-product-card__compare`
      );
      if (compareBtn) {
        compareBtn.classList.add("active");
      }
    });
  }

  goToPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarousel();
      this.updateButtons();
    }
  }

  goToNext() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateCarousel();
      this.updateButtons();
    }
  }

  updateCarousel() {
    const slideWidth = this.slides[0].offsetWidth;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    const translateX = -(this.currentIndex * (slideWidth + gap));

    this.track.style.transform = `translateX(${translateX}px)`;
  }

  updateButtons() {
    this.prevBtn.disabled = this.currentIndex === 0;
    this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize all carousels on the page
document.addEventListener("DOMContentLoaded", () => {
  const carousels = document.querySelectorAll(
    '[data-carousel^="qb-products-"]'
  );
  carousels.forEach((carousel) => {
    const instance = new QBProductCarousel(carousel);
    // Initialize saved states after carousel is created
    instance.initializeSavedStates();
  });
});

// Global event listeners for cross-carousel state management
document.addEventListener("wishlist:added", (e) => {
  // Update all wishlist buttons for this product across all carousels
  const productId = e.detail.productId;
  document
    .querySelectorAll(
      `[data-product-id="${productId}"].qb-product-card__wishlist`
    )
    .forEach((btn) => {
      btn.classList.add("active");
    });
});

document.addEventListener("wishlist:removed", (e) => {
  const productId = e.detail.productId;
  document
    .querySelectorAll(
      `[data-product-id="${productId}"].qb-product-card__wishlist`
    )
    .forEach((btn) => {
      btn.classList.remove("active");
    });
});

document.addEventListener("compare:added", (e) => {
  const productId = e.detail.productId;
  document
    .querySelectorAll(
      `[data-product-id="${productId}"].qb-product-card__compare`
    )
    .forEach((btn) => {
      btn.classList.add("active");
    });
});

document.addEventListener("compare:removed", (e) => {
  const productId = e.detail.productId;
  document
    .querySelectorAll(
      `[data-product-id="${productId}"].qb-product-card__compare`
    )
    .forEach((btn) => {
      btn.classList.remove("active");
    });
});
